import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FuseConfigService } from '@fuse/services/config';
import { AuthService } from 'app/core/auth/auth.service';
import { environment } from 'environments/environment';
import { lastValueFrom, Observable, shareReplay } from 'rxjs';

/**
 * `/v1/content/*` request/response shapes (TASK-031/TASK-032/TASK-028, `rml-platform/
 * apps/api/src/routes/content.ts` + `packages/domain/src/content/*`). Duplicated
 * locally rather than imported — same rationale as `AuthService`'s `Rml*` types
 * (`auth.service.ts`) and `MarketingService`'s own types (`marketing.service.ts`): this
 * is a standalone demo front-end repo, not a workspace package of `rml-platform` — keep
 * these in sync with the API's locked contract by hand if it ever changes.
 */
export interface RmlPlayerProgress {
    /** bigint id encoded as a JSON string (decisions/0009 D7). */
    id: string;
    conversation_id: string;
    status: string | null;
    booklet_page_number: number | null;
    video_position: number | null;
    audio_position: number | null;
    is_favourite: boolean;
    resumed_at: string | null;
    created_at: string;
    updated_at: string;
}

export interface RmlReleasedConversation {
    id: string;
    title: string;
    type: string;
    access_mode: string;
    flipbook_url: string | null;
    flipbook_url_mobile: string | null;
    stream_video_id: string | null;
    released_at: string;
    progress: RmlPlayerProgress | null;
}

export interface RmlReleasedAffirmation {
    id: string;
    conversation_id: string;
    title: string | null;
    access_mode: string;
    stream_video_id: string | null;
    /** TASK-076 — a Supabase Storage public https URL. A thought is video OR image OR
     * neither (never both) — `null` when this thought carries no image. */
    image_url: string | null;
    released_at: string;
}

export interface RmlContentListResponse {
    reflections: RmlReleasedConversation[];
    thoughts: RmlReleasedAffirmation[];
}

export interface RmlContentDetailResponse {
    reflection: RmlReleasedConversation;
}

export interface RmlProgressResponse {
    progress: RmlPlayerProgress | null;
}

/** Every field is optional/partial by design (`PUT /v1/content/progress/:conversationId`'s
 * own contract) — omit (never `null`-coerce) a field this call is not touching, or the
 * upsert will clobber whatever the caller did not intend to change. */
export interface RmlUpsertProgressRequest {
    status?: 'opened' | 'completed' | null;
    booklet_page_number?: number | null;
    video_position?: number | null;
    audio_position?: number | null;
    is_favourite?: boolean;
}

export interface RmlStreamVideoTokenResponse {
    token: string;
    expires_in: number;
}

/**
 * Maps `overall_status` used across the reflections library UI ("New" / "In Progress" /
 * "Completed") from the new API's single `player_progress.status` field
 * (`'opened' | 'completed' | null`). The legacy schema tracked THREE separate statuses
 * (`booklet_status`/`video_status`/`audio_status`) and computed a combined value
 * client-side on every progress write; the new schema already stores ONE combined
 * status server-side — never fabricated, this is a straight relabel of the same
 * three-state concept.
 */
function mapOverallStatus(status: string | null | undefined): string {
    if (status === 'completed') {
        return 'Completed';
    }
    if (status === 'opened') {
        return 'In Progress';
    }
    return 'New';
}

/**
 * Maps a `/v1/content` reflection onto the legacy `{ id, row: {...} }` shape
 * `reflections.component.ts`/`reflection-content.component.ts` already read — the
 * booklet/page-flip UI (`.team/booklet-preservation-spec.md`) is reused UNTOUCHED; only
 * this adapter changes. Fields with NO `/v1/content` equivalent (`image`/
 * `envelope_image`/`envelope_link`/`reflection_colour`/`audio_media` — no card artwork,
 * envelope artwork, accent colour, or standalone reflection audio track in the new
 * response) are mapped to `null`/harmless defaults rather than fabricated; see this
 * task's own report for the full gap list. `azure_link` (legacy video-id field) is now
 * `stream_video_id` — carried through unchanged, gates the WATCH button exactly as
 * before.
 */
function mapReflectionRow(reflection: RmlReleasedConversation): { id: string; row: Record<string, any> } {
    const progress = reflection.progress;
    return {
        id: reflection.id,
        row: {
            ref_conversation: Number(reflection.id),
            ref_conversation_title: reflection.title,
            image: null,
            envelope_image: null,
            envelope_link: null,
            reflection_colour: null,
            flipbook_url: reflection.flipbook_url,
            flipbook_url_mobile: reflection.flipbook_url_mobile,
            audio_media: null,
            azure_link: reflection.stream_video_id,
            status: mapOverallStatus(progress?.status),
            is_favourite: progress?.is_favourite ?? false,
            created: reflection.released_at,
            last_opened: progress?.resumed_at ?? null,
        },
    };
}

/**
 * Maps a `/v1/content` thought (affirmation) onto the legacy `{ id, row: {...} }` shape
 * the thoughts library/related-thoughts UI reads. `image_url`/`stream_video_id` are
 * carried through UNCHANGED (TASK-076) — a thought is image OR video OR neither, and
 * these two fields are now the ONLY signal this UI needs to actually render one (see
 * `reflections.component.ts`'s/`reflection-content.component.ts`'s own `processThought`
 * — no separate per-thought detail call is needed any more, unlike the legacy
 * `data-to-view-affirmation` payload this used to require). `name` falls back to the
 * affirmation's own `title`; `thought_type` is inferred SOLELY from `stream_video_id`
 * presence (`'Video'` vs `'Image'`) — used only to pick the LIST icon (video vs image
 * icon), never a fabricated content type — a thought with neither field renders no
 * icon-driving distinction that matters, since `processThought` itself checks
 * `image_url`/`stream_video_id` directly and renders nothing for either. `ref_conversation_title`
 * is resolved via `conversationTitleById` (built from the SAME `/v1/content` response's
 * `reflections` array) since the affirmation row itself carries only `conversation_id`,
 * not its parent's title.
 */
function mapThoughtRow(
    thought: RmlReleasedAffirmation,
    conversationTitleById: Map<string, string>,
): { id: string; row: Record<string, any> } {
    return {
        id: thought.id,
        row: {
            ref_affirmation: Number(thought.id),
            ref_conversation: Number(thought.conversation_id),
            ref_conversation_title: conversationTitleById.get(thought.conversation_id) ?? 'Untitled reflection',
            name: thought.title ?? 'Untitled thought',
            thought_type: thought.stream_video_id ? 'Video' : 'Image',
            image_url: thought.image_url,
            stream_video_id: thought.stream_video_id,
            // No legacy `release_hours` concept in the new response — `bundleThoughts()`
            // (unchanged) only ever uses this field to SORT, so a normalised epoch
            // timestamp (newest first, same as `b.release_hours - a.release_hours`)
            // preserves that sort intent without needing to touch the sort itself.
            release_hours: new Date(thought.released_at).getTime(),
        },
    };
}

@Injectable({ providedIn: 'root' })
export class JourneyService {
    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _fuseConfigService: FuseConfigService,
        private _authService: AuthService
    ) {
        this.userType = this._authService.getUserKeyValue("role");
    }
    options: object;
    payload: object;
    audioLink: string;
    showLibrary: boolean;
    readOption: boolean;
    listenOption: boolean;
    watchOption: boolean;
    imageSrc: string;
    reflectionTitle: string;
    reflectionThoughts: any[] = [];
    reflectionCollection: any[] = [];
    reflectionUnreleasedCollection: any[] = [];
    thoughtsCollection: any[] = [];
    userType: any;
    favoriteReflections: Set<number> = new Set(); // Store favorite reflection IDs locally

    // In-memory cache of `GET /v1/content` for this service instance's lifetime — both
    // `reflections` and `thoughts` come from the SAME response, so `setupReflectionCollection`/
    // `setupThoughtCollection` share one fetch instead of two (same `shareReplay(1)` pattern
    // `MarketingService`'s own per-slug cache uses).
    private _contentList$: Observable<RmlContentListResponse> | null = null;

    set showLibraryContent(value: boolean) {
        this.showLibrary = value;
    }

    get showLibraryContent(): boolean {
        return this.showLibrary
    }

    private _listContent(): Observable<RmlContentListResponse> {
        if (!this._contentList$) {
            this._contentList$ = this._httpClient
                .get<RmlContentListResponse>(`${environment.apiBase}/content`)
                .pipe(shareReplay(1));
        }
        return this._contentList$;
    }

    /**
     * `GET /v1/content` (TASK-031) — the caller's OWN released content (reflections +
     * thoughts), each reflection's own `player_progress` joined in. Replaces the legacy
     * `reflectionSchedule()`/`grid-journey-conversation-schedule` RPC call.
     */
    public async setupReflectionCollection(state: string): Promise<any> {
        const response = await lastValueFrom(this._listContent()).catch(() => {
            throw new Error("Failed to fetch reflections");
        });
        this.reflectionCollection = response.reflections.map(mapReflectionRow);
        // GAP (mapping doc §3 "Content / journey"): `/v1/content` returns RELEASED
        // content only — there is no `/v1` equivalent of the legacy "unreleased/coming
        // soon" schedule read (`grid-journey-conversation-unreleased-schedule`), so this
        // collection is always empty until that surface exists. TODO(demo): revisit if
        // an upcoming-content teaser route is ever added.
        this.reflectionUnreleasedCollection = [];
        this.favoriteReflections = new Set(
            response.reflections.filter((reflection) => reflection.progress?.is_favourite).map((reflection) => Number(reflection.id)),
        );
        return this.reflectionCollection;
    }

    /**
     * Thoughts (affirmations) come from the SAME `GET /v1/content` response as
     * `setupReflectionCollection` (no separate `/v1` list route — mapping doc §3
     * "Content / journey": "Affirmations ('thoughts') ... served WITHIN the content
     * responses"), so this reuses the shared cache rather than issuing a second call.
     */
    public async setupThoughtCollection(state: string): Promise<any> {
        const response = await lastValueFrom(this._listContent()).catch((error) => {
            throw error;
        });
        const conversationTitleById = new Map(response.reflections.map((reflection) => [reflection.id, reflection.title]));
        this.thoughtsCollection = response.thoughts.map((thought) => mapThoughtRow(thought, conversationTitleById));
        return this.thoughtsCollection;
    }

    /**
     * Bypasses the `GET /v1/content` cache above — used by `reflections.component.ts`'s
     * own background "did anything change since I loaded" banner refresh
     * (`getUpdatedBanners()`). Re-populates `reflectionCollection`/`thoughtsCollection`/
     * `favoriteReflections` exactly like the cached setup methods.
     */
    public async refreshContentCollections(): Promise<any[]> {
        this._contentList$ = null;
        await this.setupReflectionCollection("Active");
        await this.setupThoughtCollection("Active");
        return this.reflectionCollection;
    }

    /**
     * `GET /v1/content/:conversationId` (TASK-031) — a single released reflection
     * detail, the caller's OWN `player_progress` joined in. Replaces the legacy
     * `GET .../content/data-to-view-conversation?id=`.
     */
    public getContent(conversationId: number | string): Observable<RmlContentDetailResponse> {
        return this._httpClient.get<RmlContentDetailResponse>(`${environment.apiBase}/content/${conversationId}`);
    }

    /**
     * `GET /v1/content/progress/:conversationId` (TASK-028) — the caller's OWN progress
     * row for one reflection (`null` when never opened — a normal state, not an error).
     * Replaces the legacy pattern of fetching the FULL `grid-progress` list and finding
     * the matching `ref_conversation` row client-side.
     */
    public getProgress(conversationId: number | string): Observable<RmlProgressResponse> {
        return this._httpClient.get<RmlProgressResponse>(`${environment.apiBase}/content/progress/${conversationId}`);
    }

    /**
     * `PUT /v1/content/progress/:conversationId` (TASK-028) — the single upsert that
     * replaces the legacy two-step `create-progress`/`update-progress` dance. Every
     * field is optional: send ONLY what this call is actually changing (see
     * `RmlUpsertProgressRequest`'s own doc comment) — an omitted field is left
     * untouched server-side, never clobbered to `null`.
     */
    public upsertProgress(conversationId: number | string, body: RmlUpsertProgressRequest): Observable<RmlProgressResponse> {
        return this._httpClient.put<RmlProgressResponse>(`${environment.apiBase}/content/progress/${conversationId}`, body);
    }

    /**
     * `POST /v1/content/cloudflare-video-token` (TASK-032) — mints a short-lived signed
     * Cloudflare Stream token for a reflection's OWN video. Body is `{ conversation_id }`
     * ONLY — the server resolves the Stream video id itself, through the SAME
     * user-scoped released-content view `GET /v1/content` reads (security pre-check
     * U-D, `content.ts`'s own doc comment) — a client can never pass a
     * `stream_video_id` directly. This is a CONTRACT CHANGE from the legacy
     * `{ videoId }` body this method used to send; every call site now passes a
     * conversation id, never a raw video id.
     *
     * TODO(demo): implemented against the real API contract, but NOT currently called
     * by any player wiring in this demo — reflection/thought videos are presently
     * PUBLIC/unsigned on Cloudflare Stream (a deliberate product state — signed URLs
     * have a known mobile-app problem), so `reflection-content.component.ts`/
     * `embedded-videos.component.ts` feed the existing Stream player each reflection's
     * own `stream_video_id` directly (public UID playback) instead of minting a token
     * here. Kept for when signed playback is re-enabled — no code change needed on
     * this method itself, only in the two call sites above.
     */
    public cloudflareVideoToken(conversationId: number | string): Observable<RmlStreamVideoTokenResponse> {
        return this._httpClient.post<RmlStreamVideoTokenResponse>(`${environment.apiBase}/content/cloudflare-video-token`, {
            conversation_id: String(conversationId),
        });
    }

    public outputAssetUrl(image: string | null | undefined): string | null {
        if (!image) {
            // `/v1/content` has no card-artwork/envelope-artwork/audio-track field
            // equivalent (see `mapReflectionRow`'s own doc comment) — `null` here is a
            // genuine absence of data, never fabricated. Callers whose `[src]` binding
            // matters visually (the envelope pages) already gate on `*ngIf` themselves.
            return null;
        }
        const cleanImage = image.replace(/["/]/g, "");
        return this._fuseConfigService.global.settings["asset_url"] + cleanImage;
    }

    public clearCache() {
        this.options = {};
        this.payload = {};
        this.showLibrary = false;
        this.readOption = false;
        this.listenOption = false;
        this.watchOption = false;
        this.imageSrc = '';
        this.reflectionTitle = '';
        this.reflectionThoughts = [];
        this.reflectionCollection = [];
        this.reflectionUnreleasedCollection = [];
        this.thoughtsCollection = [];
        this.favoriteReflections.clear();
        this._contentList$ = null;
    }

    /**
     * Rebuilds the local favourites set from the already-loaded `reflectionCollection`
     * (its `progress.is_favourite` was already joined in by `GET /v1/content` — no
     * separate favourites/progress-list fetch needed any more, unlike the legacy
     * `grid-progress` call this used to make). Loads the collection first if it isn't
     * loaded yet (e.g. a deep link straight into `reflection-content.component.ts`).
     */
    public async loadFavoritesFromAPI(): Promise<void> {
        if (this.reflectionCollection.length === 0) {
            await this.setupReflectionCollection("Active").catch(() => null);
            return;
        }
        this.favoriteReflections = new Set(
            this.reflectionCollection.filter((reflection) => reflection.row?.is_favourite).map((reflection) => reflection.row.ref_conversation),
        );
    }

}
